import { useState } from 'react';

const TTL_OPTIONS = [
  { label: '1 hour', ms: 3_600_000 as number | null },
  { label: '24 hours', ms: 86_400_000 as number | null },
  { label: '7 days', ms: 604_800_000 as number | null },
  { label: '30 days', ms: 2_592_000_000 as number | null },
  { label: 'Never', ms: null as number | null },
];

/** Owner dialog to mint a per-note share link (scope + expiry) and copy the URL. */
export function ShareDialog({ idOrPath, onClose }: { idOrPath: string; onClose: () => void }) {
  const [scope, setScope] = useState<'read' | 'readwrite'>('read');
  const [ttlMs, setTtlMs] = useState<number | null>(86_400_000);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await fetch('/api/share', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ idOrPath, scope, ttlMs }),
      });
      const d = (await r.json()) as { url?: string; error?: string };
      if (!r.ok || !d.url) {
        setErr(d.error || 'Failed to create link');
        return;
      }
      setUrl(d.url);
    } catch {
      setErr('Failed to create link');
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="share-backdrop" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Share note</h3>
        {!url ? (
          <>
            <label className="share-field">
              <span>Access</span>
              <select value={scope} onChange={(e) => setScope(e.target.value as 'read' | 'readwrite')}>
                <option value="read">Read-only</option>
                <option value="readwrite">Read &amp; write</option>
              </select>
            </label>
            <label className="share-field">
              <span>Expires</span>
              <select
                value={ttlMs === null ? 'null' : String(ttlMs)}
                onChange={(e) => setTtlMs(e.target.value === 'null' ? null : Number(e.target.value))}
              >
                {TTL_OPTIONS.map((t) => (
                  <option key={t.label} value={t.ms === null ? 'null' : String(t.ms)}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            {err && <p className="share-err">{err}</p>}
            <div className="share-actions">
              <button onClick={onClose}>Cancel</button>
              <button className="primary" onClick={() => void create()} disabled={busy}>
                {busy ? 'Creating…' : 'Create link'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="share-hint">
              Anyone with this link can {scope === 'readwrite' ? 'read and edit' : 'read'} this note
              {ttlMs ? ' until it expires' : ''}. Revoke it anytime in Settings.
            </p>
            <div className="share-url">
              <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
              <button onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div className="share-actions">
              <button className="primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
